// components/Video.js
import { useRef, useState } from "react";

//font awsome icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import { faPause } from "@fortawesome/free-solid-svg-icons";
import { faVolumeHigh } from "@fortawesome/free-solid-svg-icons";
import { faVolumeLow } from "@fortawesome/free-solid-svg-icons";
import { faVolumeOff } from "@fortawesome/free-solid-svg-icons";
import { faVolumeMute } from "@fortawesome/free-solid-svg-icons";
import { faExpand } from "@fortawesome/free-solid-svg-icons";
import { faMinimize } from "@fortawesome/free-solid-svg-icons";

export default function Video({
  src,
  width = "100%",
  height = "100%",
  style = {},
}) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
      setIsBuffering((prev) => prev);
    } else {
      video.pause();
      setIsPlaying(false);
      setIsBuffering(false);
    }
  };

  const handleProgress = () => {
    const video = videoRef.current;
    if (video) {
      setProgress((video.currentTime / video.duration) * 100);
    }
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    if (video) {
      const newTime = (e.target.value / 100) * video.duration;
      video.currentTime = newTime;
      setProgress(e.target.value);
    }
  };

  const handleVolume = (e) => {
    const video = videoRef.current;
    if (video) {
      const newVol = e.target.value;
      video.volume = newVol;
      setVolume(newVol);
    }
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (video) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
        setIsFullScreen(false);
      } else {
        video.parentElement.requestFullscreen();
        setIsFullScreen(true);
      }
    }
  };

  const getVolumeIcon = () => {
    if (volume < 0.1)
      return <FontAwesomeIcon icon={faVolumeMute} style={{ color: "white" }} />;
    if (volume < 0.3)
      return <FontAwesomeIcon icon={faVolumeOff} style={{ color: "white" }} />;
    if (volume < 0.5)
      return <FontAwesomeIcon icon={faVolumeLow} style={{ color: "white" }} />;
    return <FontAwesomeIcon icon={faVolumeHigh} style={{ color: "white" }} />;
  };

  return (
    <div
      style={{
        position: "relative",
        width,
        height,
        backgroundColor: "#000",
        //borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        ...style,
      }}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      <video
        ref={videoRef}
        src={src}
        onTimeUpdate={handleProgress}
        onEnded={() => setIsPlaying(false)}
        onWaiting={() => setIsBuffering(true)} // video is buffering
        onPlaying={() => setIsBuffering(false)} // video resumes
        onCanPlay={() => setIsBuffering(false)} // video ready to play
        style={{
          width: "100%",
          height: "100%",
          display: "block",
          objectFit: "cover",
        }}
      />
      {/* Loading Logic */}

      {isBuffering && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "white",
            fontSize: "40px",
            pointerEvents: "none",
          }}
        >
          <div className="loader"></div>
        </div>
      )}

      {/* Controls */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          background: "linear-gradient(transparent, rgba(0, 0, 0, 0.8))",
          padding: "24px 20px 20px",
          opacity: showControls ? 1 : 0,
          transition: "opacity 0.3s ease",
          pointerEvents: showControls ? "auto" : "none",
        }}
      >
        {/* Progress bar */}
        <div style={{ marginBottom: "16px" }}>
          <input
            type="range"
            min="0"
            max="100"
            value={progress}
            onChange={handleSeek}
            style={{
              width: "100%",
              height: "6px",
              background: `linear-gradient(to right, #ff6b6b 0%, #ff6b6b ${progress}%, rgba(255,255,255,0.3) ${progress}%, rgba(255,255,255,0.3) 100%)`,
              borderRadius: "3px",
              outline: "none",
              cursor: "pointer",
              appearance: "none",
              WebkitAppearance: "none",
            }}
          />
          <style jsx>{`
            input[type="range"]::-webkit-slider-thumb {
              appearance: none;
              width: 18px;
              height: 18px;
              background: #ff6b6b;
              border-radius: 50%;
              cursor: pointer;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
              transition: transform 0.2s ease;
            }
            input[type="range"]::-webkit-slider-thumb:hover {
              transform: scale(1.2);
            }
            input[type="range"]::-moz-range-thumb {
              width: 18px;
              height: 18px;
              background: #ff6b6b;
              border-radius: 50%;
              cursor: pointer;
              border: none;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
            }
          `}</style>
        </div>

        {/* Control buttons */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Left controls */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <button
              onClick={togglePlay}
              style={{
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                border: "none",
                color: "white",
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: "15px",
                transition: "all 0.2s ease",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "rgba(255, 255, 255, 0.25)";
                e.target.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "rgba(255, 255, 255, 0.15)";
                e.target.style.transform = "scale(1)";
              }}
            >
              {isPlaying ? (
                <FontAwesomeIcon icon={faPause} />
              ) : (
                <FontAwesomeIcon icon={faPlay} />
              )}
            </button>

            {/* Volume control */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                padding: "8px 16px",
                borderRadius: "24px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <span style={{ fontSize: "18px" }}>{getVolumeIcon()}</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={handleVolume}
                style={{
                  width: "80px",
                  height: "4px",
                  background: `linear-gradient(to right, #4ecdc4 0%, #4ecdc4 ${
                    volume * 100
                  }%, rgba(255,255,255,0.3) ${
                    volume * 100
                  }%, rgba(255,255,255,0.3) 100%)`,
                  borderRadius: "2px",
                  outline: "none",
                  cursor: "pointer",
                  appearance: "none",
                  WebkitAppearance: "none",
                }}
              />
              <style jsx>{`
                input[type="range"]::-webkit-slider-thumb {
                  appearance: none;
                  width: 14px;
                  height: 14px;
                  background: #4ecdc4;
                  border-radius: 50%;
                  cursor: pointer;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                }
                input[type="range"]::-moz-range-thumb {
                  width: 14px;
                  height: 14px;
                  background: #4ecdc4;
                  border-radius: 50%;
                  cursor: pointer;
                  border: none;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                }
              `}</style>
            </div>
          </div>

          {/* Right controls */}
          <button
            onClick={toggleFullscreen}
            style={{
              background: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(10px)",
              border: "none",
              color: "white",
              width: "44px",
              height: "44px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: "18px",
              transition: "all 0.2s ease",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(255, 255, 255, 0.25)";
              e.target.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(255, 255, 255, 0.15)";
              e.target.style.transform = "scale(1)";
            }}
          >
            {isFullScreen ? (
              <FontAwesomeIcon icon={faMinimize} />
            ) : (
              <FontAwesomeIcon icon={faExpand} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
